import { useState } from 'react';
import { Search, Eye, Clock, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-emerald-100 text-emerald-700',
  preparing: 'bg-blue-100 text-blue-700',
  ready: 'bg-purple-100 text-purple-700',
  delivered: 'bg-stone-100 text-stone-600',
  cancelled: 'bg-red-100 text-red-700'
};

const statusLabels = {
  pending: 'Pendiente',
  paid: 'Pagado',
  preparing: 'Preparando',
  ready: 'Listo',
  delivered: 'Entregado',
  cancelled: 'Cancelado'
};

export default function OrdersTab({ orders, companies, onUpdateStatus }) {
  const [filters, setFilters] = useState({
    status: 'all',
    company: 'all',
    search: ''
  });
  const [selectedOrder, setSelectedOrder] = useState(null);

  const filteredOrders = orders.filter(order => {
    if (filters.status !== 'all' && order.status !== filters.status) return false;
    if (filters.company !== 'all' && order.customer?.company !== filters.company) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      if (!order.order_id?.toLowerCase().includes(search) &&
          !order.user_name?.toLowerCase().includes(search) &&
          !order.user_email?.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  const updateOrderStatus = async (orderId, newStatus) => {
    if (onUpdateStatus) {
      await onUpdateStatus(orderId, newStatus);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-stone-900">Gestión de Pedidos</h1>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              placeholder="Buscar pedido..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              className="pl-9 w-48"
            />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pending">Pendiente</SelectItem>
              <SelectItem value="paid">Pagado</SelectItem>
              <SelectItem value="preparing">Preparando</SelectItem>
              <SelectItem value="ready">Listo</SelectItem>
              <SelectItem value="delivered">Entregado</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.company} onValueChange={(v) => setFilters({...filters, company: v})}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Empresa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-stone-600">Pedido</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-stone-600">Cliente</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-stone-600">Empresa</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-stone-600">Total</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-stone-600">Estado</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-stone-600">Fecha</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-stone-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {filteredOrders.map(order => (
                <tr key={order.order_id} className="hover:bg-stone-50">
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-stone-600">
                      {order.order_id?.slice(-8)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-stone-900">{order.user_name}</p>
                      <p className="text-sm text-stone-500">{order.user_email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-600">
                    {order.customer?.company || '-'}
                  </td>
                  <td className="px-4 py-3 font-semibold text-stone-900">
                    ${order.total?.toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={statusColors[order.status]}>
                      {statusLabels[order.status]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-sm text-stone-600">
                    {order.created_at ? format(new Date(order.created_at), 'dd MMM HH:mm', { locale: es }) : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedOrder(order)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Detalle del Pedido</DialogTitle>
                        </DialogHeader>
                        {selectedOrder && (
                          <div className="space-y-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm text-stone-500">Pedido</p>
                                <p className="font-mono">{selectedOrder.order_id}</p>
                              </div>
                              <Badge className={statusColors[selectedOrder.status]}>
                                {statusLabels[selectedOrder.status]}
                              </Badge>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm text-stone-500">Cliente</p>
                                <p className="font-medium">{selectedOrder.user_name}</p>
                              </div>
                              <div>
                                <p className="text-sm text-stone-500">Empresa</p>
                                <p className="font-medium">{selectedOrder.customer?.company || '-'}</p>
                              </div>
                            </div>

                            {selectedOrder.customer && (
                              <div className="bg-stone-50 rounded-lg p-3 flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-stone-400" />
                                <span className="text-sm">
                                  Piso {selectedOrder.customer.floor}, {selectedOrder.customer.work_area}
                                </span>
                              </div>
                            )}

                            <div>
                              <p className="text-sm text-stone-500 mb-2">Productos</p>
                              <div className="space-y-2">
                                {selectedOrder.items?.map((item, idx) => (
                                  <div key={idx} className="flex justify-between items-center bg-stone-50 rounded-lg p-2">
                                    <div className="flex items-center gap-2">
                                      {item.image_url && (
                                        <img src={item.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                                      )}
                                      <div>
                                        <p className="font-medium text-sm">{item.name}</p>
                                        <p className="text-xs text-stone-500">x{item.quantity}</p>
                                      </div>
                                    </div>
                                    <p className="font-medium">${(item.price * item.quantity).toLocaleString()}</p>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="border-t pt-3 flex justify-between items-center">
                              <span className="font-semibold">Total</span>
                              <span className="text-xl font-bold text-emerald-700">
                                ${selectedOrder.total?.toLocaleString()}
                              </span>
                            </div>

                            {selectedOrder.status !== 'delivered' && selectedOrder.status !== 'cancelled' && (
                              <div className="flex gap-2 pt-2">
                                {selectedOrder.status === 'paid' && (
                                  <Button 
                                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                                    onClick={() => updateOrderStatus(selectedOrder.order_id, 'preparing')}
                                  >
                                    Iniciar Preparación
                                  </Button>
                                )}
                                {selectedOrder.status === 'preparing' && (
                                  <Button 
                                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                                    onClick={() => updateOrderStatus(selectedOrder.order_id, 'ready')}
                                  >
                                    Marcar Listo
                                  </Button>
                                )}
                                {selectedOrder.status === 'ready' && (
                                  <Button 
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                    onClick={() => updateOrderStatus(selectedOrder.order_id, 'delivered')}
                                  >
                                    Marcar Entregado
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredOrders.length === 0 && (
          <div className="text-center py-12 text-stone-500">
            No hay pedidos que coincidan con los filtros
          </div>
        )}
      </div>
    </div>
  );
}
